import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera } from './components/Camera';
import { GoalSetting, type DetectorMode } from './components/GoalSetting';
import { SessionUI } from './components/SessionUI';
import { SessionSummary } from './components/SessionSummary';
import { TrackingOverlay } from './components/TrackingOverlay';
import { SimpleJugglingDetector } from './utils/SimpleJugglingDetector';
import { JugglingDetector } from './utils/JugglingDetector';
import { JuggleVisionPipeline } from './lib/JuggleVisionPipeline';
import { SessionTracker } from './utils/SessionTracker';
import { playThrowSound, playGoalReachedSound } from './utils/sounds';
import { cn } from './utils/cn';
import { ReplayRecorder, type ReplayFrame } from './utils/ReplayRecorder';
import type { BallTrack } from './lib/BallTracker';
import type { HandPosition } from './lib/HandTracker';

type AppState = 'SETUP' | 'SESSION' | 'SUMMARY';

const TRAIL_LENGTH = 20;

export function App() {
  const [appState, setAppState] = useState<AppState>('SETUP');
  const [sessionData, setSessionData] = useState(new SessionTracker().getData());
  const [dropFlash, setDropFlash] = useState(false);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [detectionZone, setDetectionZone] = useState<{ x: number; y: number; width: number; height: number } | undefined>();
  const [detectorLoading, setDetectorLoading] = useState(false);
  const [detectorError, setDetectorError] = useState<string | null>(null);
  const [replayFrames, setReplayFrames] = useState<ReplayFrame[] | null>(null);
  const [tracks, setTracks] = useState<BallTrack[]>([]);
  const [hands, setHands] = useState<HandPosition[]>([]);
  const [trailHistory, setTrailHistory] = useState<Array<{ id: number; x: number; y: number }[]>>([]);
  const [pipelineStats, setPipelineStats] = useState<{
    leftPct: number;
    rightPct: number;
    meanPeakHeight: number;
    totalThrows: number;
    totalCatches: number;
  } | null>(null);
  const [videoDimensions, setVideoDimensions] = useState({ width: 640, height: 480 });
  const [detectorMode, setDetectorMode] = useState<DetectorMode>('yolo');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const detectorRef = useRef<SimpleJugglingDetector | JugglingDetector>(new SimpleJugglingDetector(3));
  const pipelineRef = useRef<JuggleVisionPipeline | null>(null);
  const detectorModeRef = useRef<DetectorMode>('yolo');
  const trackerRef = useRef(new SessionTracker());
  const animationFrameRef = useRef<number | undefined>(undefined);
  const replayRecorderRef = useRef<ReplayRecorder | null>(null);
  const trailMapRef = useRef<Map<number, Array<{ x: number; y: number }>>>(new Map());

  const handleStartSession = useCallback(
    async (
      throwsGoal?: number,
      timeGoal?: number,
      calibrationTime?: number,
      mode: DetectorMode = 'yolo'
    ) => {
      trackerRef.current.setGoals(throwsGoal, timeGoal);
      detectorModeRef.current = mode;
      setDetectorMode(mode);
      setDetectorError(null);
      setReplayFrames(null);
      setTracks([]);
      setHands([]);
      setTrailHistory([]);
      setPipelineStats(null);
      trailMapRef.current.clear();

      if (mode === 'yolo') {
        const pipeline = new JuggleVisionPipeline();
        pipelineRef.current = pipeline;
        setAppState('SESSION');
        setDetectorLoading(true);
        try {
          await pipeline.initialize();
          if (!pipeline.isReady()) {
            throw new Error(pipeline.getError() ?? 'YOLO model not loaded');
          }
          setDetectorLoading(false);
          replayRecorderRef.current = new ReplayRecorder();
          replayRecorderRef.current.start();
          pipeline.reset();
          startYoloDetection();
        } catch (err) {
          setDetectorLoading(false);
          setDetectorError(
            err instanceof Error ? err.message : 'YOLO model not found. Train a model and add yolo-juggle.onnx to public/models/. Use Demo mode for now.'
          );
          pipelineRef.current = null;
        }
        return;
      }

      if (mode === 'motion') {
        const detector = new SimpleJugglingDetector(calibrationTime ?? 3);
        detectorRef.current = detector;
        if (videoRef.current && videoRef.current.videoWidth > 0) {
          setDetectionZone(detector.getDetectionZone(videoRef.current.videoWidth, videoRef.current.videoHeight));
        }
        setAppState('SESSION');
        replayRecorderRef.current = new ReplayRecorder();
        replayRecorderRef.current.start();
        startLegacyDetection();
        return;
      }

      const detector = new JugglingDetector(calibrationTime ?? 3);
      detectorRef.current = detector;
      setAppState('SESSION');
      setDetectorLoading(true);
      try {
        await detector.initialize();
        setDetectorLoading(false);
        if (videoRef.current && videoRef.current.videoWidth > 0) {
          setDetectionZone(detector.getDetectionZone(videoRef.current.videoWidth, videoRef.current.videoHeight));
        }
        replayRecorderRef.current = new ReplayRecorder();
        replayRecorderRef.current.start();
        startLegacyDetection();
      } catch (err) {
        setDetectorLoading(false);
        setDetectorError(err instanceof Error ? err.message : 'Failed to load ball detection model');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startYoloDetection and startLegacyDetection are stable
    []
  );

  const startYoloDetection = useCallback(() => {
    const detect = async () => {
      const video = videoRef.current;
      const pipeline = pipelineRef.current;
      const recorder = replayRecorderRef.current;
      if (!video || !pipeline || !recorder) return;

      const timestampMs = performance.now();
      const result = await pipeline.processFrame(video, timestampMs, recorder);

      if (result.error) {
        setDetectorError(result.error);
        return;
      }

      setTracks(result.tracks);
      setHands(result.hands);
      setPipelineStats({
        leftPct: result.leftPct,
        rightPct: result.rightPct,
        meanPeakHeight: result.meanPeakHeight,
        totalThrows: result.totalThrows,
        totalCatches: result.totalCatches,
      });

      const trailMap = trailMapRef.current;
      for (const t of result.tracks) {
        let arr = trailMap.get(t.id);
        if (!arr) {
          arr = [];
          trailMap.set(t.id, arr);
        }
        arr.push({ x: t.x, y: t.y });
        if (arr.length > TRAIL_LENGTH) arr.shift();
      }
      setTrailHistory(
        Array.from(trailMap.entries()).map(([id, arr]) =>
          arr.map((p) => ({ id, x: p.x, y: p.y }))
        )
      );

      if (result.tracks.length > 0) {
        trackerRef.current.onJugglingDetected();
      } else {
        trackerRef.current.onJugglingStopped();
      }

      if (result.throwCount > 0) {
        playThrowSound();
        for (let i = 0; i < result.throwCount; i++) trackerRef.current.onThrowDetected();
      }

      const data = trackerRef.current.update();
      setSessionData(data);

      if (data.state === 'ENDED') {
        playGoalReachedSound();
        setAppState('SUMMARY');
        return;
      }

      animationFrameRef.current = requestAnimationFrame(detect);
    };
    detect();
  }, []);

  const startLegacyDetection = useCallback(() => {
    const detect = () => {
      const video = videoRef.current;
      const detector = detectorRef.current;
      if (!video) return;

      const mode = detectorModeRef.current;
      let result: {
        isJuggling: boolean;
        throwDetected: boolean;
        throwCount?: number;
        dropDetected: boolean;
        isCalibrating: boolean;
        balls?: ReadonlyArray<{ id: number; x: number; y: number; prevY: number }>;
      };
      if (mode === 'object' && detector instanceof JugglingDetector && detector.isReady()) {
        result = detector.detect(video, Math.round(performance.now()));
      } else if (mode === 'motion' && detector instanceof SimpleJugglingDetector) {
        result = detector.detect(video);
      } else {
        result = { isJuggling: false, throwDetected: false, dropDetected: false, isCalibrating: true };
      }

      setIsCalibrating(result.isCalibrating);

      if (!result.isCalibrating && result.balls && replayRecorderRef.current) {
        replayRecorderRef.current.recordFrame(performance.now(), result.balls);
      }

      if (result.isJuggling) {
        trackerRef.current.onJugglingDetected();
      } else {
        trackerRef.current.onJugglingStopped();
      }

      const throwsToCount = result.throwCount ?? (result.throwDetected ? 1 : 0);
      if (throwsToCount > 0) {
        playThrowSound();
        for (let i = 0; i < throwsToCount; i++) trackerRef.current.onThrowDetected();
      }

      if (result.dropDetected) {
        trackerRef.current.onDropDetected();
        setDropFlash(true);
        setTimeout(() => setDropFlash(false), 300);
      }

      const data = trackerRef.current.update();
      setSessionData(data);

      if (data.state === 'ENDED') {
        playGoalReachedSound();
        setAppState('SUMMARY');
        return;
      }

      animationFrameRef.current = requestAnimationFrame(detect);
    };
    detect();
  }, []);

  const handleEndSession = useCallback(() => {
    trackerRef.current.end();
    setSessionData(trackerRef.current.getData());
    if (replayRecorderRef.current) {
      const frames = replayRecorderRef.current.stop();
      setReplayFrames(frames);
    }
    setAppState('SUMMARY');
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  const handleNewSession = useCallback(() => {
    trackerRef.current.reset();
    setSessionData(trackerRef.current.getData());
    setAppState('SETUP');
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  const handleVideoReady = useCallback((video: HTMLVideoElement) => {
    videoRef.current = video;
    setVideoDimensions({ width: video.videoWidth || 640, height: video.videoHeight || 480 });
    const detector = detectorRef.current;
    if (video.videoWidth > 0 && detector && 'getDetectionZone' in detector) {
      const zone = detector.getDetectionZone(video.videoWidth, video.videoHeight);
      setDetectionZone(zone);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div className={cn('min-h-screen bg-gray-100 p-5 max-w-3xl mx-auto')}>
      <h1 className="text-center text-2xl font-semibold text-gray-900">JuggleVision</h1>

      {appState !== 'SUMMARY' && (
        <div className="mb-5 relative">
          <Camera
            onVideoReady={handleVideoReady}
            showDetectionZone={appState === 'SESSION' && detectorMode !== 'yolo'}
            detectionZone={detectionZone}
          />
          {appState === 'SESSION' && detectorMode === 'yolo' && (
            <TrackingOverlay
              width={videoDimensions.width}
              height={videoDimensions.height}
              tracks={tracks}
              hands={hands}
              trailHistory={trailHistory}
            />
          )}
          {isCalibrating && appState === 'SESSION' && detectorMode !== 'yolo' && (
            <div
              className="text-center py-4 mt-2 text-xl font-bold rounded border-2 border-amber-400 bg-amber-50 text-amber-900"
              role="status"
              aria-live="polite"
            >
              Calibrating... Get into position and stand still
            </div>
          )}
        </div>
      )}

      {appState === 'SETUP' && (
        <GoalSetting onStartSession={handleStartSession} defaultDetectorMode="yolo" />
      )}

      {appState === 'SESSION' && detectorLoading && (
        <div className="text-center py-12 text-lg text-gray-600" role="status" aria-live="polite">
          Loading YOLO and hand tracking models…
        </div>
      )}

      {appState === 'SESSION' && detectorError && (
        <div className="text-center py-8 px-4 rounded border-2 border-red-200 bg-red-50 text-red-800">
          <p className="font-semibold">Could not load YOLO detector</p>
          <p className="mt-2 text-sm">{detectorError}</p>
          <p className="mt-2 text-sm">Use Demo mode (Object) to try with round objects, or add yolo-juggle.onnx per JuggleVisionSpec.md.</p>
        </div>
      )}

      {appState === 'SESSION' && !detectorLoading && !detectorError && (
        <SessionUI
          sessionData={sessionData}
          onEndSession={handleEndSession}
          dropFlash={dropFlash}
          pipelineStats={pipelineStats}
        />
      )}

      {appState === 'SUMMARY' && (
        <SessionSummary
          sessionData={sessionData}
          onNewSession={handleNewSession}
          replayFrames={replayFrames ?? undefined}
          pipelineStats={pipelineStats}
          videoDimensions={videoDimensions}
        />
      )}
    </div>
  );
}
